<template>
  <v-dialog :value="value" max-width="760" scrollable :fullscreen="$vuetify.breakpoint.xsOnly" @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="pb-0">Quick guide</v-card-title>
      <v-tabs v-model="tab" grow class="flex-grow-0">
        <v-tab>{{ $vuetify.breakpoint.xsOnly ? "2D" : "2D simulator (Meep)" }}</v-tab>
        <v-tab>{{ $vuetify.breakpoint.xsOnly ? "3D" : "3D simulator (Gmsh + NGSolve)" }}</v-tab>
      </v-tabs>
      <v-divider />
      <v-card-text class="pt-2">
        <v-tabs-items v-model="tab">
          <v-tab-item>
          <h3 class="text-subtitle-1 mt-2">Units</h3>
          <ul>
            <li>Lengths, positions, sizes and wavelengths are in <b>µm</b>.</li>
            <li>Frequency is in <b>c/µm</b>: f = 1/λ. Example: λ = 1.55 µm → f = 0.645 c/µm.</li>
            <li>Time ("Until") is in <b>µm/c</b>: light travels 1 µm in 1 unit of time.</li>
            <li>Materials store the <b>refractive index n</b>; the simulator uses ε = n².</li>
            <li>The center of the cell is (0, 0); y grows upwards.</li>
          </ul>

          <h3 class="text-subtitle-1 mt-4">Step by step</h3>
          <ol>
            <li><b>Cell</b> (no object selected): size in µm, resolution, simulation time and the
              absorbing border (PML). The material list here sets the <b>background</b> of the cell.</li>
            <li><b>Geometries</b>: choose the shape in the toolbar and drag on the canvas. Select it to
              set size, position, rotation and material. When shapes overlap, the last one wins.</li>
            <li><b>Sources</b>: choose the source tool and click. Continuous wave = one wavelength
              (good for field pictures and videos); Gaussian pulse = a band of wavelengths;
              Gaussian beam = focused beam with a direction.</li>
            <li><b>Outputs</b> (optional): transmission / reflection lines measure spectra; a
              resonance point gives the resonant frequencies and Q (Harminv).</li>
            <li><b>Run simulation</b> and open the results: dielectric map, field, video and spectra.</li>
          </ol>

          <h3 class="text-subtitle-1 mt-4">Good practice (for correct numbers)</h3>
          <ul>
            <li><b>Resolution</b>: at least 10 pixels per wavelength <i>inside</i> the densest material
              (resolution ≥ 10·n/λ) to see the field; about 20 for spectra with ~2–3% error and
              40 for ~0.5%. The error drops ~4× each time the resolution doubles: run again with
              double resolution and check that the result barely changes.</li>
            <li><b>PML</b>: thickness of about half a wavelength or more. Keep sources, monitors and
              structures out of it (only waveguides, substrates and plane-wave sources should
              cross it). Leave at least 0.5 µm between outputs, sources, objects and the PML.</li>
            <li><b>Plane waves</b> (interfaces, slabs, gratings): in Cell Properties set
              <b>Borders</b> to "PML in x, periodic in y" and make the source and the outputs span
              the full height. With PML on all sides a plane wave becomes a truncated beam and R
              can be off by ~30%.</li>
            <li><b>Spectra</b> are normalized: the simulator also runs without your structure (or with
              the geometries marked "reference") and divides the results. With one transmission and
              one reflection output, check that the loss 1 − R − T is close to 0 for lossless materials.</li>
            <li><b>Resonances</b>: put the point inside the cavity and use a Gaussian pulse source
              whose bandwidth Δf covers the expected mode. For sharp resonances set
              <b>Spectra accuracy</b> to "High" (slower, but the error in the spectra can drop
              from ~10% to ~1%).</li>
            <li>Start from an <b>example</b> and compare your result with its reference values.</li>
          </ul>
          </v-tab-item>

          <v-tab-item>
            <h3 class="text-subtitle-1 mt-2">What the 3D simulator does</h3>
            <p class="mb-1">
              It computes the <b>steady field</b> of your sources at <b>one wavelength</b> around 3D
              objects, with the finite element method: Gmsh builds a mesh of the scene and NGSolve
              solves Maxwell's equations on it. Use it to see how light is scattered, concentrated
              or guided by a 3D shape.
            </p>
            <p class="mb-0">
              It does <b>not</b> give spectra, resonances or videos (there is no time evolution).
              For those, use the 2D simulator.
            </p>

            <h3 class="text-subtitle-1 mt-4">Units and axes</h3>
            <ul>
              <li>Positions, sizes and wavelengths are in <b>µm</b>.</li>
              <li>Each object is placed by its <b>center</b> (x, y, z) and fits in a box of
                width (x) × height (y) × depth (z). Cylinders, cones and pyramids stand along
                <b>y</b> (base at the bottom); prisms and rings are drawn in the xy plane and
                extruded along <b>z</b>.</li>
              <li>Materials store the <b>refractive index n</b>; the solver uses ε = n².</li>
              <li>There is no cell to set: an air box with a margin of about one wavelength is
                created automatically around the objects and sources.</li>
            </ul>

            <h3 class="text-subtitle-1 mt-4">Step by step</h3>
            <ol>
              <li><b>Geometry</b>: choose the shape in the toolbar (box, sphere, triangular, rhombic or
                trapezoidal prism, cylinder, cone, pyramid, ring) and click the canvas once: one object is
                created and the tool returns to Select (dragging only rotates the view). The object appears at
                (0, 0, 0) with 1 × 1 × 1 µm. Select it to set <b>Dimensions</b>, <b>Position</b> and
                <b>Material</b> (pick one from the list: new objects start with ε = 12). Where
                objects overlap, the last one wins.</li>
              <li><b>Source</b>: choose the source tool and click the canvas. It is a small Gaussian
                current <b>polarized along x</b>, also created at (0, 0, 0): <b>move it out of the
                object</b> (e.g. 0.5–1 µm away). Set the wavelength λ and the source size σ.</li>
              <li><b>Accuracy</b> (Simulation panel or the Run window): <i>Fast</i> for a preview,
                <i>Accurate</i> (recommended) for numbers, <i>Reference</i> only for very small
                scenes.</li>
              <li><b>Run</b>. The results page shows the field on a cut through the objects and a 3D
                view with the energy flow, plus the size of the problem that was solved.</li>
            </ol>

            <h3 class="text-subtitle-1 mt-4">Reading the results</h3>
            <ul>
              <li><b>Field on a cut</b>: |Re(E)| on the horizontal plane through the center of the
                objects. Bright = strong field; outlines = objects; star = source (its z is written
                next to it, because the source may be outside the cut).</li>
              <li><b>Geometries and energy flow</b>: arrows give the direction of the Poynting vector
                S = ½ Re(E × H*), i.e. where the energy goes; their color follows |S| on a log scale.</li>
              <li>The field of an x-polarized source is strongest above and below it (±y and ±z) and
                vanishes along x: two bright lobes next to the source are expected.</li>
            </ul>

            <h3 class="text-subtitle-1 mt-4">Good practice (for correct numbers)</h3>
            <ul>
              <li><b>Keep the scene compact</b>: objects up to about 1–1.5 µm and sources close to
                them. Larger scenes are solved with first-order elements and the result is only
                qualitative (errors of 30–60%); the results page warns when this happens.</li>
              <li><b>Source size</b>: σ well below λ/2 (e.g. 0.2–0.4 µm at λ = 1.55 µm).</li>
              <li>All sources use the <b>wavelength of the first source</b> (single frequency) and
                are always polarized along x.</li>
              <li>Measured accuracy with <i>Accurate</i>: about 4–12% error in the field for ε up to
                4 and up to ~20% for ε ≈ 12 (silicon); <i>Fast</i> is 2–4× worse. Check important
                results by running again with a finer preset and comparing.</li>
              <li>Metals and absorbing materials are not supported (constant ε, no losses).</li>
            </ul>
          </v-tab-item>
        </v-tabs-items>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text color="primary" @click="$emit('input', false)">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
// Guia rápido dos simuladores 2D e 3D (aberto pelo botão de ajuda e pelo menu no celular).
// Abre na aba do simulador em uso.
export default {
  name: "QuickGuideDialog",
  props: {
    value: { type: Boolean, default: false }
  },
  data: () => ({
    tab: 0
  }),
  watch: {
    value(open) {
      if (open) this.tab = /3d/i.test(this.$route.path) ? 1 : 0;
    }
  }
};
</script>
